import React, { useState, useRef, useEffect } from 'react';
import './TempleMap.css';

const OVERPASS_API = 'https://overpass-api.de/api/interpreter';

export default function TempleMap() {
  const [city, setCity] = useState('');
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState(null);
  const [searched, setSearched] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  // Dynamically load Leaflet CSS and JS
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => initMap();
      document.body.appendChild(script);
    } else {
      initMap();
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  function initMap() {
    if (mapInstanceRef.current || !mapRef.current || !window.L) return;

    const map = window.L.map(mapRef.current).setView([20.5937, 78.9629], 5); // India center
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    mapInstanceRef.current = map;
  }

  function clearMarkers() {
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
  }

  async function geocodeCity(cityName) {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityName)}&limit=1`,
      { headers: { 'Accept': 'application/json' } }
    );
    const data = await res.json();
    if (!data.length) return null;
    return {
      lat: parseFloat(data[0].lat),
      lon: parseFloat(data[0].lon),
      boundingbox: data[0].boundingbox.map(Number),
      displayName: data[0].display_name,
    };
  }

  async function fetchHinduTemples(bbox) {
    // bbox: [south, north, west, east]
    const query = `
      [out:json][timeout:30];
      (
        node["amenity"="place_of_worship"]["religion"="hindu"](${bbox[0]},${bbox[2]},${bbox[1]},${bbox[3]});
        way["amenity"="place_of_worship"]["religion"="hindu"](${bbox[0]},${bbox[2]},${bbox[1]},${bbox[3]});
        relation["amenity"="place_of_worship"]["religion"="hindu"](${bbox[0]},${bbox[2]},${bbox[1]},${bbox[3]});
      );
      out center;
    `;

    const res = await fetch(OVERPASS_API, {
      method: 'POST',
      body: `data=${encodeURIComponent(query)}`,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    if (!res.ok) throw new Error('Failed to fetch temple data');
    const data = await res.json();
    return data.elements.map(el => ({
      id: el.id,
      name: el.tags?.name || 'Hindu Temple',
      lat: el.lat || el.center?.lat,
      lon: el.lon || el.center?.lon,
      deity: el.tags?.denomination || el.tags?.['name:en'] || '',
      address: el.tags?.['addr:full'] || el.tags?.['addr:street'] || '',
      website: el.tags?.website || '',
    })).filter(t => t.lat && t.lon);
  }

  function plotTemples(templeList) {
    if (!mapInstanceRef.current || !window.L) return;

    clearMarkers();
    const map = mapInstanceRef.current;

    const templeIcon = window.L.divIcon({
      html: '<span style="font-size:24px;">🛕</span>',
      className: 'temple-marker-icon',
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30],
    });

    templeList.forEach(temple => {
      const popup = `
        <div class="temple-popup">
          <h3>${escapeHtml(temple.name)}</h3>
          ${temple.deity ? `<p>🙏 ${escapeHtml(temple.deity)}</p>` : ''}
          ${temple.address ? `<p>📍 ${escapeHtml(temple.address)}</p>` : ''}
          ${temple.website ? `<p><a href="${escapeHtml(temple.website)}" target="_blank" rel="noopener noreferrer">🌐 Website</a></p>` : ''}
          <p><a href="https://www.openstreetmap.org/node/${temple.id}" target="_blank" rel="noopener noreferrer">View on OSM</a></p>
        </div>
      `;

      const marker = window.L.marker([temple.lat, temple.lon], { icon: templeIcon })
        .addTo(map)
        .bindPopup(popup);

      markersRef.current.push(marker);
    });

    if (templeList.length > 0) {
      const bounds = window.L.latLngBounds(templeList.map(t => [t.lat, t.lon]));
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  async function handleSearch(e) {
    e.preventDefault();
    const trimmed = city.trim();
    if (!trimmed) return;

    setLoading(true);
    setError('');
    setTemples([]);
    setSearched(true);

    try {
      const geo = await geocodeCity(trimmed);
      if (!geo) {
        setError(`Could not find "${trimmed}". Please try a different city/town name.`);
        setLoading(false);
        return;
      }

      setMapCenter({ lat: geo.lat, lon: geo.lon });

      const templeData = await fetchHinduTemples(geo.boundingbox);
      setTemples(templeData);
      plotTemples(templeData);

      if (templeData.length === 0) {
        setError(`No Hindu temples found in ${geo.displayName}. Try a larger city or check spelling.`);
      }
    } catch (err) {
      setError('Error fetching temple data. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="temple-map-page">
      <h1>🛕 Hindu Temple Finder</h1>
      <p className="subtitle">Find Hindu temples in any city or town using OpenStreetMap</p>

      <form className="temple-search-form" onSubmit={handleSearch}>
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Enter city or town name (e.g., Varanasi)"
          aria-label="City or town name"
        />
        <button type="submit" disabled={loading || !city.trim()}>
          {loading ? 'Searching...' : '🔍 Find Temples'}
        </button>
      </form>

      {loading && (
        <div className="temple-loading">
          <span className="spinner"></span> Searching for Hindu temples...
        </div>
      )}

      {error && <div className="temple-error">{error}</div>}

      {searched && !loading && !error && temples.length > 0 && (
        <div className="temple-results-info">
          Found <span className="count">{temples.length}</span> Hindu temple{temples.length !== 1 ? 's' : ''}
        </div>
      )}

      <div className="temple-map-container">
        <div ref={mapRef} style={{ width: '100%', height: '100%' }}></div>
      </div>
    </div>
  );
}
